<template>
  <div class="space-y-3">
    <!-- Top 5 produits -->
    <div v-for="(product, index) in top5Products" :key="product.CPRODUCT"
      class="p-3 rounded-xl product-item">

      <div class="flex items-center gap-3">
        <!-- Rang -->
        <div
          class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-semibold text-xs"
          :class="getRankColorClass(index)">
          {{ index + 1 }}
        </div>

        <!-- Contenu principal -->
        <div class="flex-1 min-w-0 space-y-2">
          <!-- Header : nom + CA + navigation -->
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <h3
                class="font-semibold text-foreground text-sm leading-tight truncate">
                {{ product.CDGN }}</h3>
              <p class="text-xs text-muted-foreground truncate">{{ product.CFAMILY_NAME
              }}</p>
            </div>

            <span class="flex-shrink-0 font-bold text-foreground text-sm">{{
              formatCurrencyForCenter(product.total_ati, center) }}</span>
          </div>

          <!-- Métriques compactes -->
          <div class="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {{ $t('top5products.avg_per_sales', {
                line_count: product.line_count, avg_basket:
                  getAverageBasket(product)
              }) }}
            </span>
            <span>{{ getMarketShare(product) }}</span>
          </div>

          <!-- Barre de progression -->
          <div class="flex items-center gap-2">
            <div class="flex-1 h-1.5 rounded-full overflow-hidden progress-track">
              <div
                class="h-full rounded-full transition-all duration-300 progress-fill"
                :style="{ width: getProgressWidth(product.total_ati) + '%' }">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Séparateur -->
    <div v-if="lowestSaleProduct" class="border-t my-4 separator"></div>

    <!-- Plus faible vente -->
    <div v-if="lowestSaleProduct"
      class="p-3 rounded-xl product-item">

      <div class="flex items-center gap-3">
        <!-- Badge dernier -->
        <div
          class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-semibold text-xs rank-lowest">
          {{ totalProductsCount }}
        </div>

        <!-- Contenu principal -->
        <div class="flex-1 min-w-0 space-y-2">
          <!-- Header avec label -->
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-xs font-medium text-red-500">
                  {{ $t('top5products.lowest_sale') }}
                </span>
              </div>
              <h3
                class="font-semibold text-foreground text-sm leading-tight truncate">
                {{ lowestSaleProduct.CDGN }}</h3>
              <p class="text-xs text-muted-foreground truncate">{{
                lowestSaleProduct.CFAMILY_NAME }}</p>
            </div>

            <span class="flex-shrink-0 font-bold text-red-500 text-sm">{{
              formatCurrencyForCenter(lowestSaleProduct.total_ati, center) }}</span>
          </div>

          <!-- Métriques -->
          <div class="flex items-center justify-between text-xs text-muted-foreground">
            <span>{{ $t('top5products.avg_per_sales', {
              line_count:
                lowestSaleProduct.line_count, avg_basket:
                getAverageBasket(lowestSaleProduct)
            }) }}</span>
            <span>{{ getMarketShare(lowestSaleProduct) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Message si pas de données -->
    <div v-if="!top5Products || top5Products.length === 0"
      class="text-center text-muted-foreground py-8">
      {{ $t('top5products.no_products') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ProductsReportResponse } from '../responses'
import { Center } from '@/user';
import { formatCurrencyForCenter } from '@/utils/Languages';

interface Props {
  productsReport: ProductsReportResponse | null,
  center: Center
}

const props = defineProps<Props>()

const top5Products = computed(() => {
  return props.productsReport?.period_summary?.top_5_products || []
})

const maxRevenue = computed(() => {
  if (!top5Products.value || top5Products.value.length === 0) return 0
  return Math.max(...top5Products.value.map(p => p.total_ati))
})

// Couleur selon le rang
const getRankColorClass = (index: number): string => {
  const classes = ['rank-1', 'rank-2', 'rank-3', 'rank-default', 'rank-default']
  return classes[index] || 'rank-default'
}

// Largeur de la barre de progression
const getProgressWidth = (revenue: number): number => {
  if (maxRevenue.value === 0) return 0
  return Math.round((revenue / maxRevenue.value) * 100)
}

// Métriques de performance par produit
const getAverageBasket = (product: any): string => {
  if (product.line_count === 0) return formatCurrencyForCenter(0, props.center)
  const averageBasket = product.total_ati / product.line_count
  return formatCurrencyForCenter(averageBasket, props.center)
}

const getMarketShare = (product: any): string => {
  if (!props.productsReport?.period_summary?.total_revenue) return '0%'
  const marketShare = (product.total_ati / props.productsReport.period_summary.total_revenue.included_tax) * 100
  return `${Math.round(marketShare)}%`
}

const totalProductsCount = computed(() => {
  return props.productsReport?.period_summary?.total_products_count || 0
})

// Produit avec la plus faible vente (basé sur tous les produits, excluant les 0)
const lowestSaleProduct = computed(() => {
  if (!props.productsReport?.products || props.productsReport.products.length === 0) return null

  // Trouve le produit avec le plus faible CA parmi tous les produits (excluant les )
  const sortedProducts = props.productsReport.products
    .map(product => product.product_data)
    .filter(product => product.tax_data.included_tax > 0) // Exclure les produits à 0
    .sort((a, b) => a.tax_data.included_tax - b.tax_data.included_tax)

  if (sortedProducts.length === 0) return null

  const lowestProduct = sortedProducts[0]

  // Formatage pour correspondre à la structure attendue
  return {
    CDGN: lowestProduct.product_name,
    CFAMILY_NAME: lowestProduct.family_name,
    total_ati: lowestProduct.tax_data.included_tax,
    line_count: lowestProduct.line_count
  }
})
</script>

<style scoped>
.product-item {
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-card);
  transition: background 0.15s ease;
}

.product-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

:is(.dark *).product-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.rank-1 {
  background: var(--cyan-subtle);
  color: var(--apex-cyan);
  border: 1px solid var(--cyan-border);
}

.rank-2 {
  background: rgba(148, 163, 184, 0.25);
  color: var(--foreground);
  border: 1px solid rgba(148, 163, 184, 0.35);
}

.rank-3 {
  background: rgba(180, 130, 80, 0.10);
  color: rgb(180, 130, 80);
  border: 1px solid rgba(180, 130, 80, 0.15);
}

:is(.dark *).rank-3 {
  color: rgb(210, 165, 110);
}

.rank-default {
  background: rgba(148, 163, 184, 0.10);
  color: var(--foreground);
  border: 1px solid rgba(148, 163, 184, 0.20);
}

.rank-lowest {
  background: rgba(239, 68, 68, 0.10);
  color: rgb(239, 68, 68);
  border: 1px solid rgba(239, 68, 68, 0.15);
}

.progress-track {
  background: var(--border-subtle);
}

.progress-fill {
  background: var(--apex-cyan);
}

.separator {
  border-color: var(--border-subtle);
}
</style>